/**
 * Parse a date + time typed by a staffer, EXPLICITLY as America/New_York
 * local time (DST-aware, via Intl — not the browser's local timezone),
 * and return the equivalent UTC ISO string.
 *
 * Client-side twin of server/lib/scheduling/etTime.ts (unit-tested
 * identically). Kept pure/dependency-free (no luxon in the client bundle)
 * using the standard Intl.DateTimeFormat round-trip technique: format a
 * guess in the target zone, diff against the desired wall time, and
 * correct — converges in one or two passes even across a DST boundary.
 *
 * Fixes the ScheduleMeetingModal bug where
 * `new Date(`${date}T${time}:00`).toISOString()` parsed in the BROWSER'S
 * local timezone despite the field being labeled "Time (ET)".
 *
 * @param date "YYYY-MM-DD"
 * @param time "HH:mm" (24-hour)
 */
export function parseEasternDateTime(date: string, time: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) {
    throw new Error(`parseEasternDateTime: invalid date/time "${date} ${time}"`);
  }
  const desiredUtcMs = Date.UTC(y, m - 1, d, hh, mm, 0);

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  // Format a candidate instant, reading its America/New_York wall-clock
  // numbers back as if they were UTC (for diffing against desiredUtcMs).
  const format = (ms: number): number => {
    const parts = formatter.formatToParts(new Date(ms));
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    // Intl can format midnight as "24" for hour12:false — normalize.
    const hour = get("hour") === 24 ? 0 : get("hour");
    return Date.UTC(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
  };

  // Pass 1: guess the offset from the desired wall-clock numbers read AS IF
  // UTC (wildly wrong day/offset, but converges fast).
  const afterFirstPass = desiredUtcMs + (desiredUtcMs - format(desiredUtcMs));

  // Pass 2: refine against the (now much closer) guess. Always enough for
  // a VALID, unambiguous local time — the ET offset only ever changes by 1
  // hour, so pass 1 already lands within that hour.
  const secondDiff = desiredUtcMs - format(afterFirstPass);
  if (secondDiff === 0) return new Date(afterFirstPass).toISOString();
  const afterSecondPass = afterFirstPass + secondDiff;

  // Pass 3: a local time that FALLS IN THE SPRING-FORWARD GAP (e.g. 2:30 AM
  // on the date the clocks jump from 2:00 to 3:00, which the clock never
  // shows) has no fixed point — pass 2's correction overshoots into the
  // pre-transition offset, and re-checking it here comes back nonzero
  // again (oscillating between the two candidates) instead of converging.
  // Detect that and resolve the gap by shifting FORWARD past it
  // (afterFirstPass, the post-transition candidate) — matching luxon's
  // (the server twin's) default DST-gap-handling convention.
  const thirdDiff = desiredUtcMs - format(afterSecondPass);
  if (thirdDiff === 0) return new Date(afterSecondPass).toISOString();
  return new Date(afterFirstPass).toISOString();
}
